import React, { useMemo, useCallback } from 'react';
import '../styles/PropertyCardDetail.css';
import { colour, formatterLong, numberFormatter } from '../styles/GlobalStyles';
import { Badge, Button, CompCardMap, CompDetails, Condition, CustomSwiper, Description, Disqualifier, FinishChip, IconButton, PropertyDistance, PropertyIcon, PropertyLinks, PropertyObstructions, Timeline } from './';
import { Bathroom, Bedroom, Chevron, Exit, Floodzone, Image, LotSize, SquareFootage, YearBuilt } from '../assets/icons';
import { formatDaysOnMarket, getFlipDetails, getPriceField, getStatusDotColour, propertyConditionBG, propertyStatusBG, propertyStatusText, propertyTypeFormat, squareFeetToAcres } from '../functions';
import { getTopDrivers } from '../utils/driverUtils';
import { getDriverLabel } from '../utils/driverLabels';
import { HugeiconsIcon } from '@hugeicons/react';
import { BlockGameIcon, Calendar03Icon, CarParking01Icon, CarParking02Icon, PoolIcon } from '@hugeicons/core-free-icons';

function PropertyCardDetail(props) {
    const property = props.property;
    const selected = props.selected;
    const closeDetailedView = props.closeDetailedView;
    const toggleSelectedComp = props.toggleSelectedComp;
    const showScrollableImages = props.showScrollableImages;
    const propertyType = property.propertyAttributes.propertyType;
    const isLoaded = props.isLoaded;
    const subjectPropertyObj = props.subjectProperty;
    const readOnly = props.readOnly;
    // Determine if this property is the subject property
    const isSubjectProperty = useMemo(() => {
        if (!subjectPropertyObj) return false;
        if (typeof subjectPropertyObj === 'boolean') return subjectPropertyObj;
        // Compare property IDs to determine if this is the subject property
        return subjectPropertyObj.uniqueIds?.propertyId === property.uniqueIds?.propertyId;
    }, [subjectPropertyObj, property.uniqueIds?.propertyId]);
    const bestComp = props.bestComp;
    const loading = props.loading;
    const clickedCompDescription = props.clickedCompDescription;
    const conditionUpdate = props.conditionUpdate;
    const allComps = props.allComps;
    const propertyEstimates = props.propertyEstimates || null;
    const sourceURL = props.sourceURL || null;
    const source = props.source || null;
    const subjectPropertyAnalysis = props.subjectPropertyAnalysis || null;
    const remodelLevel = props.remodelLevel ?? null;
    // const subdivisions = props.subdivisions;
    // const subdivisionObject = subdivisions.find(subdivision => 
    //     subdivision.children && subdivision.children.includes(property.propertyAttributes.subdivision)
    // );
    
    // Find matching curb appeal data by zpid, or use subjectPropertyAnalysis for subject property
    const curbAppealData = useMemo(() => {
        const curbAppeals = props.curbAppeals || [];
        // For subject property, use subjectPropertyAnalysis if available
        if (isSubjectProperty) {
            if (subjectPropertyAnalysis) {
                return {
                    positiveDrivers: Array.isArray(subjectPropertyAnalysis.positiveDrivers) ? subjectPropertyAnalysis.positiveDrivers : [],
                    negativeDrivers: Array.isArray(subjectPropertyAnalysis.negativeDrivers) ? subjectPropertyAnalysis.negativeDrivers : [],
                    externalPositive: subjectPropertyAnalysis.externalPositive || null,
                    externalNegative: subjectPropertyAnalysis.externalNegative || null,
                    noiseRating: subjectPropertyAnalysis.noiseRating || null
                };
            }
            return null;
        }
        
        // For comps, look up in curbAppeals array
        if (!Array.isArray(curbAppeals) || curbAppeals.length === 0) {
            return null;
        }
        const zpid = property.uniqueIds?.zillowId;
        if (!zpid) return null;
        return curbAppeals.find(item => {
            const itemZpid = typeof item.zpid === "string" ? item.zpid : `${item.zpid}`;
            const propertyZpid = typeof zpid === "string" ? zpid : `${zpid}`;
            return itemZpid === propertyZpid;
        }) || null;
    }, [isSubjectProperty, props.curbAppeals, subjectPropertyAnalysis, property.uniqueIds?.zillowId]);
    
    // Get top drivers and their labels
    const topDrivers = useMemo(() => {
        if (!curbAppealData) {
            return { topPositive: null, topNegative: null, topPositiveLabel: null, topNegativeLabel: null };
        }
        const drivers = getTopDrivers(curbAppealData);
        return {
            topPositive: drivers.topPositive,
            topNegative: drivers.topNegative,
            topPositiveLabel: drivers.topPositive ? getDriverLabel(drivers.topPositive) : null,
            topNegativeLabel: drivers.topNegative ? getDriverLabel(drivers.topNegative) : null
        };
    }, [curbAppealData]);
    
    const finalCondition = isSubjectProperty ? null : conditionUpdate.findIndex(e => e.zpid === property.uniqueIds.zillowId) !== -1 ? conditionUpdate[conditionUpdate.findIndex(e => e.zpid === property.uniqueIds.zillowId)].condition : property.compDetails.propertyCondition;
    const price = getPriceField(property) === null ? "Price unavailable" : formatterLong.format(getPriceField(property)).replace(".00", "");
    const flipDetails = isSubjectProperty ? null : getFlipDetails(property);
    const flipStatus = flipDetails === null ? null : flipDetails.flipStatus;
    const flipBGColour = flipDetails === null ? null : flipDetails.flipBGColour;
    const flipDescription = flipDetails === null ? null : flipDetails.flipDescription;
    const flipDotcolour = flipDetails === null ? null : flipDetails.flipDotcolour;
    const date = property.listingDetails.listingStatus === "for_sale" ? formatDaysOnMarket(property.listingDetails.saleListingDate) : formatDaysOnMarket(property.listingDetails.soldListingDate);
    const constructionType = property.propertyAttributes.constructionTypeBySource === undefined || property.propertyAttributes.constructionTypeBySource === null || property.propertyAttributes.constructionTypeBySource.reapi === null ? property.propertyAttributes.constructionType : property.propertyAttributes.constructionTypeBySource.reapi;
    const floodZoneType = property.propertyAttributes.floodZoneType ? property.propertyAttributes.floodZoneType : "D";
    const floodZoneCodes = typeof floodZoneType === "string" ? floodZoneType : floodZoneType.join(",");

    // Function to generate garage label based on available data
    const getGarageLabel = useCallback((isSubject, hasGarage, propertyAttributes) => {
        // For subject property: return "Garage" if it has a garage, otherwise null
        if (isSubject === true) {
            return hasGarage === true ? "Garage" : null;
        }
        
        // For comp properties: return null if no garage
        if (hasGarage !== true) {
            return null;
        }
        
        // Check for garageSpaces (integer) and garageAttachment ("attached"/"detached")
        const garageSpaces = propertyAttributes?.garageSpaces;
        const garageAttachment = propertyAttributes?.garageAttachment;
        
        const hasSpaces = garageSpaces !== null && garageSpaces !== undefined && typeof garageSpaces === 'number' && garageSpaces > 0;
        const hasAttachment = garageAttachment === "attached" || garageAttachment === "detached";
        
        // If we have both spaces and attachment info
        if (hasSpaces && hasAttachment) {
            const attachmentAbbr = garageAttachment === "attached" ? "att." : "det.";
            return `${garageSpaces} car garage (${attachmentAbbr})`;
        }
        
        // If we only have spaces info
        if (hasSpaces) {
            return `${garageSpaces} car garage`;
        }
        
        // If we only know there's a garage (no spaces or attachment info)
        return "Garage";
    }, []);

    // Memoize garage label
    const garageLabel = useMemo(() =>
        getGarageLabel(isSubjectProperty, property.propertyAttributes.garage, property.propertyAttributes),
        [isSubjectProperty, property.propertyAttributes, getGarageLabel]
    );

    // Function to generate carport label based on available data
    const getCarportLabel = useCallback((isSubject, hasCarport, propertyAttributes) => {
        if (isSubject === true) {
            return hasCarport === true ? "Carport" : null;
        }
        if (hasCarport !== true) {
            return null;
        }
        const carportSpaces = propertyAttributes?.carportSpaces;
        const hasSpaces = carportSpaces !== null && carportSpaces !== undefined && typeof carportSpaces === 'number' && carportSpaces > 0;
        if (hasSpaces) {
            return `${carportSpaces} car carport`;
        }
        return "Carport";
    }, []);

    // Memoize carport label
    const carportLabel = useMemo(() =>
        getCarportLabel(isSubjectProperty, property.propertyAttributes.carport, property.propertyAttributes),
        [isSubjectProperty, property.propertyAttributes, getCarportLabel]
    );

    const propertyDetails = [
        {
            label: propertyTypeFormat(propertyType),
            icon: <PropertyIcon propertyType={propertyType} fill={colour.gray04} className="" size={14} />
        },
        {
            label: `${property.propertyAttributes.bedrooms} beds`,
            icon: <Bedroom className="" size={14} fill={colour.gray04} />
        },
        {
            label: `${property.propertyAttributes.bathrooms} baths`,
            icon: <Bathroom className="" size={14} fill={colour.gray04} />
        },
        {
            label: `${numberFormatter.format(property.propertyAttributes.livingSquareFootage)} sqft`,
            icon: <SquareFootage fill={colour.gray04} className="" />
        },
        {
            label: `Built ${property.propertyAttributes.yearBuilt}`,
            icon: <YearBuilt fill={colour.gray04} className="" />
        },
        {
            label: `${squareFeetToAcres(property.propertyAttributes.lotSquareFootage)}`,
            icon: <LotSize fill={colour.gray04} className="" />
        },
        {
            label: garageLabel,
            icon: <HugeiconsIcon icon={CarParking01Icon} size={16} color={colour.gray05} />
        },
        {
            label: carportLabel,
            icon: <HugeiconsIcon icon={CarParking02Icon} size={16} color={colour.gray05} />
        },
        {
            label: property.propertyAttributes.pool === true ? "Pool" : null,
            icon: <HugeiconsIcon icon={PoolIcon} size={16} color={colour.gray05} />
        },
        {
            label: constructionType,
            icon: <HugeiconsIcon icon={BlockGameIcon} size={16} color={colour.gray05} />
        },
        {
            label: `Flood Zone ${floodZoneCodes}`,
            icon: <Floodzone className="property-card-floodzone-icon" fill={colour.gray05} />
        }
    ];
    
    return (
        <div className="property-card-detail-outer-container">
            <div className="property-card-detail-exit-container">
                 <IconButton
                    size="medium"
                    icon={<Exit fill={colour.gray06} className="" size={16} clickFunc={() => null} />}
                    disabled={false}
                    clickFunc={() => closeDetailedView(false)}
                    standardIcon={false}
                    background={true}
                    bgType={"white"}
                    contextTooltip={false}
                    contextTooltipBG={null}
                    contextTooltipText={null}
                    customClass={null}
                />
            </div>
            <div className="property-card-detail-heading-container">
                <h2 className="heading-large-bold colour-primary">
                    {price}
                </h2>
                <span className="body-regular colour-secondary block-text">
                    {property.addressDetails.streetAddress}, {property.addressDetails.city}, {property.addressDetails.state} {property.addressDetails.postalCode}
                </span>
                <PropertyLinks
                    property={property}
                    darkMode={false}
                    propertyEstimates={isSubjectProperty ? propertyEstimates : null}
                    sourceURL={sourceURL}
                    source={source}
                    subjectProperty={isSubjectProperty}
                />
                {
                    isSubjectProperty === true && remodelLevel !== null && remodelLevel !== undefined ?
                    <FinishChip
                        label={remodelLevel === 0 ? "Lipstick" : remodelLevel === 1 ? "Mid-tier" : "Luxury"}
                        level={remodelLevel}
                    />
                    :
                    null
                }
            </div>
            <div className="property-card-detail-body-container">
                {
                    (topDrivers.topPositive || topDrivers.topNegative) &&
                    <div className="property-card-detail-driver-badges-container">
                        {topDrivers.topPositive && topDrivers.topPositiveLabel && (
                            <Badge
                                bgColour="bg-colour-jade-03-dark"
                                text={topDrivers.topPositiveLabel}
                                statusDot={false}
                                statusDotColour={null}
                                textClass="body-semibold colour-white"
                                icon={null}
                                tooltip={false}
                                tooltipBody={null}
                                tooltipPosition={null}
                                iconLeft={false}
                            />
                        )}
                        {topDrivers.topNegative && topDrivers.topNegativeLabel && (
                            <Badge
                                bgColour="bg-colour-red-03-dark"
                                text={topDrivers.topNegativeLabel}
                                statusDot={false}
                                statusDotColour={null}
                                textClass="body-semibold colour-white"
                                icon={null}
                                tooltip={false}
                                tooltipBody={null}
                                tooltipPosition={null}
                                iconLeft={false}
                            />
                        )}
                    </div>
                }
                <div className="property-card-detail-images-container">
                    {
                        property.listingDetails.mlsPhotoUrls === null || property.listingDetails.mlsPhotoUrls.length === 0 ?
                        <div 
                            className="medium-comp-card-no-image-container"
                        >
                            <Image
                                className="medium-comp-card-no-image-element"
                                fill={colour.gray05}
                            />
                            <span className="label-regular colour-secondary block-text text-align-center">
                                No image
                            </span>
                        </div>
                        :
                        property.listingDetails.mlsPhotoUrls.length === 1 ?
                        <div 
                            className="medium-comp-card-single-image-container"
                            style={{ backgroundImage: `url(${property.listingDetails.mlsPhotoUrls[0]})` }}
                        >
                        </div>
                        :
                        <CustomSwiper
                            images={property.listingDetails.mlsPhotoUrls}
                            imageLimit={100}
                            title=""
                            arrowClicked={null}
                            imageClick={() => showScrollableImages(property)}
                        />
                    }
                </div>
                <div className="property-card-detail-info-container">
                    {
                        propertyDetails.map((item, index) =>
                            item.label !== null &&
                            <div 
                                className="property-card-detail-element"
                                key={index}
                            >
                                {item.icon}
                                <span className="body-regular colour-primary">
                                    {item.label}
                                </span>
                            </div>
                        )
                    }
                </div>
                <div className="property-card-detail-badges-row">
                    <Badge
                        bgColour={propertyStatusBG(property, false)}
                        text={propertyStatusText(property)}
                        statusDot={true}
                        statusDotColour={getStatusDotColour(property)}
                        textClass="body-semibold colour-primary"
                        icon={null}
                        tooltip={false}
                        tooltipBody={null}
                        tooltipPosition={null}
                        iconLeft={false}
                    />
                    {
                        (flipStatus === "Flip" || flipStatus === "Flop") &&
                        <div className="relative-container">
                            <Badge
                                bgColour={flipBGColour}
                                text={flipStatus}
                                statusDot={true}
                                statusDotColour={flipDotcolour}
                                textClass="body-semibold colour-primary"
                                icon={null}
                                tooltip={true}
                                tooltipBody={flipDescription}
                                tooltipPosition={"top"}
                                iconLeft={false}
                            />
                        </div>
                    }
                </div>
                <div className="property-card-detail-date-container">
                    <HugeiconsIcon 
                        icon={Calendar03Icon}
                        color={colour.gray04} 
                        className="" 
                        size={16} 
                    />
                    <span className="body-regular colour-primary">
                        {property.listingDetails.listingStatus === "for_sale" ? "Listed " : "Sold "}
                        <span className="body-bold">
                            {date}
                        </span>
                    </span>
                </div>
                <div className="property-card-detail-grey-divider">
                </div>
                <Condition
                    label={finalCondition}
                    iconBG={propertyConditionBG(finalCondition, false)}
                    darkMode={false}
                />
                {
                    property.listingDetails.listingDescription !== null &&
                    <Description
                        property={property}
                    />
                }
                <div className="property-card-detail-grey-divider">
                </div>
                {
                    property.compDetails.arvGeneratedNotes !== undefined && property.compDetails.arvGeneratedNotes !== false &&
                    <CompDetails
                        details={property.compDetails.arvGeneratedNotes.updates}
                    />
                }
                {/* {
                    subdivisionObject &&
                    <SubdivisionWarning
                        subdivisionObject={subdivisionObject}
                    />
                } */}
                {
                    property.compDetails.arvGeneratedNotes !== undefined && property.compDetails.arvGeneratedNotes !== false &&
                    <div className="property-card-detail-grey-divider">
                    </div>
                }
                <div className="property-card-detail-map-container">
                    <PropertyDistance
                        property={property}
                    />
                    <PropertyObstructions
                        property={property}
                    />
                    <CompCardMap
                        isMapLoaded={isLoaded}
                        comp={property}
                        subjectProperty={subjectPropertyObj}
                        isMapActive={true}
                        bestComp={bestComp}
                    />
                </div>
                <div className="property-card-detail-grey-divider">
                </div>
                {
                    property.compDetails.arvPriceHistory !== null &&
                    <Timeline
                        data={property.compDetails.arvPriceHistory}
                        forSale={property.listingDetails.listingStatus === "for_sale" ? true : false}
                    />
                }
                {
                    bestComp === false && (clickedCompDescription !== null || loading === true) &&
                    <Disqualifier
                        text={clickedCompDescription}
                        loading={false}
                        key={clickedCompDescription}
                        allComps={allComps}
                    />
                }
            </div>
            <div className="property-card-detail-buttons-container">
                <div 
                    className="property-card-detail-cancel-button text-button"
                    onClick={() => closeDetailedView()}
                >
                    <Chevron
                        fill={colour.gray06}
                        className="reverse"
                    />
                    <span className="body-regular text-link">
                        Back
                    </span>
                </div>
                {
                    readOnly === false &&
                    <div className="">
                        <Button
                            buttonType={selected === true ? "light-button" : "primary-button"}
                            size="large"
                            text={selected === true ? "Remove" : "Keep"}
                            cta={() => toggleSelectedComp(property)}
                            disabled={false}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={false}
                        />
                    </div>
                }
            </div>
        </div>
    )
};

export default React.memo(PropertyCardDetail);